import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Cell,
} from "recharts";
import {
  useMeters,
  useSolarExportImport,
  useSolarNetBalance,
  useSolarTimeline,
  useSolarSelfSufficiency,
} from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
  StatCard,
} from "../components/common";
import { fmt, CHART_COLORS } from "../utils/helpers";
import { Sun } from "lucide-react";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function SolarPage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);

  const id = meterId || meters[0];

  const { data: balanceData, isLoading: l1 } = useSolarNetBalance(id, days);
  const { data: exportImport, isLoading: l2 } = useSolarExportImport(id, days);
  const { data: timeline, isLoading: l3 } = useSolarTimeline(id, days);
  const { data: selfSuff, isLoading: l4 } = useSolarSelfSufficiency(id, days);

  const balance = balanceData;
  const dailyData = exportImport?.data ?? [];
  const timeData = timeline?.data ?? [];
  const suffData = selfSuff?.data ?? [];

  const avgSuff = suffData.length
    ? suffData.reduce((s: number, d: any) => s + d.self_sufficiency_pct, 0) /
      suffData.length
    : 0;

  const tt =
    (color1: string, color2: string) =>
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="ra-tooltip">
          <p className="text-slate-500 mb-1">{label}</p>
          {payload[0] && (
            <p style={{ color: color1 }}>
              {payload[0].name}: {payload[0].value?.toFixed(3)}
            </p>
          )}
          {payload[1] && (
            <p style={{ color: color2 }}>
              {payload[1].name}: {payload[1].value?.toFixed(3)}
            </p>
          )}
        </div>
      );
    };

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Solar Integration"
          subtitle="Simulated solar export — real data flows automatically when solar meters go live"
        >
          <MeterSelect meters={meters} value={id} onChange={setMeterId} />
          <DaysSelect
            value={days}
            onChange={setDays}
            options={[7, 14, 30, 60, 90]}
          />
        </PageHeader>

        {/* Simulated notice */}
        <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 w-fit">
          <Sun size={14} className="text-amber-500" />
          <span className="text-xs text-amber-700 font-medium">
            Simulated data — bell-curve pattern peaking at 1 PM. Switches to
            real data when solar meters go live.
          </span>
        </div>

        {/* KPI row */}
        {l1 ? (
          <Spinner />
        ) : (
          balance && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="Total Exported"
                value={fmt.num(balance.total_export_kwh, 2)}
                unit="kWh"
                color="emerald"
              />
              <StatCard
                label="Total Imported"
                value={fmt.num(balance.total_import_kwh, 2)}
                unit="kWh"
                color="blue"
              />
              <StatCard
                label="Net Balance"
                value={fmt.num(Math.abs(balance.net_kwh), 2)}
                unit="kWh"
                color={balance.is_net_exporter ? "emerald" : "amber"}
              />
              <StatCard
                label="Avg Self-Sufficiency"
                value={fmt.num(avgSuff, 1)}
                unit="%"
                color="violet"
              />
            </div>
          )
        )}

        <ChartCard
          title="Daily Solar Export vs Grid Import"
          subtitle="kWh per day"
        >
          {l2 ? (
            <Spinner />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={dailyData}
                margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.grid}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#64748B", fontSize: 10 }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={tt(CHART_COLORS.blue, CHART_COLORS.emerald)}
                />
                <Bar
                  dataKey="import_kwh"
                  name="Grid Import"
                  fill={CHART_COLORS.blue}
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="export_kwh"
                  name="Solar Export"
                  fill={CHART_COLORS.emerald}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <ChartCard
            title="Solar Export — Time of Day"
            subtitle="Average watts by hour"
          >
            {l3 ? (
              <Spinner />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart
                  data={timeData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_COLORS.grid}
                  />
                  <XAxis
                    dataKey="time_slot"
                    tick={{ fill: "#64748B", fontSize: 9 }}
                    tickLine={false}
                    interval={7}
                  />
                  <YAxis
                    tick={{ fill: "#64748B", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="ra-tooltip">
                          <p className="text-slate-500 mb-1">{label}</p>
                          <p className="text-amber-600">
                            {payload[0]?.value?.toFixed(1)} W avg export
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="avg_export_w"
                    stroke={CHART_COLORS.amber}
                    fill={CHART_COLORS.amber}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    name="Avg Export W"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard
            title="Self-Sufficiency %"
            subtitle="% of daily consumption covered by solar"
          >
            {l4 ? (
              <Spinner />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={suffData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_COLORS.grid}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#64748B", fontSize: 9 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: "#64748B", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className="ra-tooltip">
                          <p className="text-slate-500 mb-1">{label}</p>
                          <p className="text-violet-600">
                            Self-sufficiency:{" "}
                            {d?.self_sufficiency_pct?.toFixed(1)}%
                          </p>
                          <p className="text-emerald-600">
                            Solar hours: {d?.solar_hours?.toFixed(1)}h
                          </p>
                          <p className="text-slate-500">
                            Grid hours: {d?.grid_dependency_hours?.toFixed(1)}h
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="self_sufficiency_pct"
                    name="Self Sufficiency %"
                    radius={[3, 3, 0, 0]}
                  >
                    {suffData.map((d: any, i: number) => (
                      <Cell
                        key={i}
                        fill={
                          d.self_sufficiency_pct >= 50
                            ? CHART_COLORS.emerald
                            : d.self_sufficiency_pct >= 25
                              ? CHART_COLORS.amber
                              : CHART_COLORS.blue
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        <div className="ra-card mt-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">
            Daily Net Balance
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  {[
                    "Date",
                    "Grid Import (kWh)",
                    "Solar Export (kWh)",
                    "Net (kWh)",
                    "Status",
                  ].map((h) => (
                    <th key={h} className="text-left pb-2 pr-4 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...dailyData]
                  .reverse()
                  .slice(0, 14)
                  .map((d: any, i: number) => (
                    <tr
                      key={i}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-2 pr-4 text-slate-500 font-mono">
                        {d.date}
                      </td>
                      <td className="py-2 pr-4 font-mono text-blue-600">
                        {fmt.num(d.import_kwh, 3)}
                      </td>
                      <td className="py-2 pr-4 font-mono text-emerald-600">
                        {fmt.num(d.export_kwh, 3)}
                      </td>
                      <td className="py-2 pr-4 font-mono text-slate-800 font-semibold">
                        {fmt.num(Math.abs(d.net_kwh), 3)}
                      </td>
                      <td className="py-2">
                        <span
                          className={
                            d.net_status === "exporting"
                              ? "ra-badge-green"
                              : "ra-badge-blue"
                          }
                        >
                          {d.net_status === "exporting"
                            ? "Net exporter"
                            : "Net importer"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
